import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import wordCloud from './c-wordcloud';
import cloneDeep from 'lodash.clonedeep';
import { throttle } from 'throttle-debounce';

import './style.scss';

const THROTTLE_DELAY = 2000;

const propsAreEqual = (prev, next) => {
    return JSON.stringify(prev.list) === JSON.stringify(next.list) && prev.resized === next.resized;
};

const CWordCloud = ({ words = {}, colors = [], maxFontSize, minFontSize, style = {} }) => {
    const containerRef = useRef(null);
    const timeoutRef = useRef(null);

    const [list, setList] = useState({});
    const [displayWords, setDisplayWords] = useState([]);
    const [resized, setResized] = useState(false);

    const updateList = throttle(THROTTLE_DELAY, wordsList => setList(wordsList));

    const filterDeletedWords = useCallback((list) => {
        const deletedWords = displayWords.filter(({ word }) => !list[word]).map(({ word }) => word);
        if (deletedWords.length) {
            setDisplayWords(displayWords => displayWords.filter(({ word }) => !deletedWords.includes(word)))
        }
    }, [displayWords]);

    useEffect(() => {
        if (Object.keys(words).length) {
            updateList(words);
        }
    }, [words]);

    useEffect(() => {
        filterDeletedWords(list);
    }, [list]);

    useEffect(() => {
        if (containerRef.current) {
            const observer = new ResizeObserver(() => {
                setResized(true);
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => setResized(false), THROTTLE_DELAY / 4)
            });

            observer.observe(containerRef.current);

            return () => {
                observer.disconnect();
                clearTimeout(timeoutRef.current);
            };
        }
    }, []);

    return <div className='c-wordcloud-container' style={style} ref={containerRef}>
        <Cloud list={list} colors={colors} resized={resized} maxFontSize={maxFontSize} minFontSize={minFontSize} setDisplayWords={setDisplayWords} />
        {displayWords.map(({ word, left, top, fontSize, color }) => <span className='c-wordcloud-word' key={word} style={{ color, fontSize, left, top }} >
            {word}
        </span>)}
    </div>
};

export default CWordCloud;

const Cloud = memo(function Cloud({ list = {}, colors = [], resized, maxFontSize = 80, minFontSize = 10, setDisplayWords = () => { } }) {
    const wordCloudRef = useRef(null);

    useEffect(() => {
        if (!wordCloudRef.current || !Object.keys(list).length) return;

        wordCloudRef.current.width = wordCloudRef.current.offsetWidth;
        wordCloudRef.current.height = wordCloudRef.current.offsetHeight;

        const fontFamily = window.getComputedStyle(document.body).getPropertyValue('font-family');

        wordCloud(wordCloudRef.current, {
            list: Object.keys(list).map(word => [word, list[word]]).sort((a, b) => b[1] - a[1]),
            colors,
            fontFamily,
            minFontSize,
            maxFontSize,
        });

        return () => wordCloud.stop();

    }, [list, resized]);

    useEffect(() => {
        const wordDrawnListener = ({ detail }) => {
            const { word, data } = detail;

            setDisplayWords(displayWords => {
                const _displayWords = cloneDeep(displayWords);
                const index = displayWords.findIndex(displayWord => displayWord.word === word);

                if (index < 0) {
                    _displayWords.push({ word, ...data })
                } else {
                    _displayWords[index] = { ..._displayWords[index], ...data }
                }

                return _displayWords;
            })
        };

        if (wordCloudRef.current) {
            wordCloudRef.current.addEventListener('wordcloudtextdrawn', wordDrawnListener);
        }

        return () => wordCloudRef.current?.removeEventListener('wordcloudtextdrawn', wordDrawnListener);

    }, [wordCloudRef])

    return <canvas style={{ width: '100%', height: '100%' }} ref={wordCloudRef} />

}, propsAreEqual);